import React from 'react';

/**
 * The plate window, in one place.
 *
 * DESIGN.md names this as a component of the world — "a photographic region with a
 * hairline frame, corner ticks and a reference ID" — and it now appears in three
 * listings and a reading view. Defining it once is what keeps it a system: a plate
 * is a plate because there is one definition of it, not because four files happened
 * to agree. `Chrome.tsx` exists for the same reason.
 */

/**
 * The four registration ticks.
 *
 * They sit one pixel outside the frame on each corner, so they read as survey marks
 * cut across the rule rather than as a second border inside it. `HubItemRow` leaves
 * them off on purpose — at 112px wide they read as dirt on the sheet — so they are
 * opt-in rather than baked into every frame.
 */
export const CornerTicks: React.FC = () => (
  <>
    <span className="pointer-events-none absolute -left-px -top-px z-10 h-2 w-2 border-l border-t border-rule-strong" />
    <span className="pointer-events-none absolute -right-px -top-px z-10 h-2 w-2 border-r border-t border-rule-strong" />
    <span className="pointer-events-none absolute -bottom-px -left-px z-10 h-2 w-2 border-b border-l border-rule-strong" />
    <span className="pointer-events-none absolute -bottom-px -right-px z-10 h-2 w-2 border-b border-r border-rule-strong" />
  </>
);

interface PlateWindowProps {
  /** Empty renders the reserved state instead. */
  pictureUrl: string;
  /**
   * What to name in the plate when there is no picture. This is a *reserved* plate,
   * not a stand-in: the round-1 reviewer's words were that it "names the absent asset
   * rather than standing in for one". Pass something true — the piece's category, say
   * — or pass nothing and get an honestly empty frame. Never filler.
   */
  reservedLabel?: string;
  /**
   * The mounted treatment: a second hairline frame inset from the first, the way a
   * plate is mounted on a sheet. Used by the news feed, where the plate is a third of
   * the row and can carry the extra rule without closing up.
   */
  mounted?: boolean;
  /** Applied to the image, for hover transitions owned by the calling row. */
  imageClassName?: string;
}

export const PlateWindow: React.FC<PlateWindowProps> = ({
  pictureUrl,
  reservedLabel,
  mounted = false,
  imageClassName = '',
}) => {
  const content = pictureUrl ? (
    <img src={pictureUrl} alt="" className={`h-full w-full object-cover ${imageClassName}`} />
  ) : reservedLabel ? (
    <div className="flex h-full w-full items-center justify-center p-6">
      <span className="text-center font-display text-step-1 italic leading-snug text-ink-muted">
        {reservedLabel}
      </span>
    </div>
  ) : null;

  return (
    <div
      className={`relative aspect-[4/3] w-full border border-rule-strong bg-page-sunk ${
        mounted ? 'p-2' : 'overflow-hidden'
      }`}
    >
      <CornerTicks />
      {mounted ? (
        <div className="relative h-full w-full overflow-hidden border border-rule-strong">
          {content}
        </div>
      ) : (
        content
      )}
    </div>
  );
};

export default PlateWindow;
